/**
 * WarmupHeader — the daily-games header. Uses the app's real chrome (coin balance chip, notification
 * bell + dropdown, and the global hamburger menu) so it matches every other screen; only the optional
 * back button (to leave a game and return to the hub) is warmup-specific.
 */

import { useCallback, useState } from 'react';
import { View, Pressable } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { ChevronLeft, Menu } from 'lucide-react-native';
import { warmupHeaderStyles as styles } from './WarmupHeader.styles';
import { WARMUP_COLORS } from '../../../src/quiz/constants/warmup.constants';
import { CreateCoinBalanceChip } from '../../create/CreateCoinBalanceChip/CreateCoinBalanceChip';
import { GameLeaderboardTrophy } from '../GameLeaderboardSheet/GameLeaderboardTrophy';
import { NotificationBell } from '../../notifications/NotificationBell/NotificationBell';
import { NotificationDropdown } from '../../notifications/NotificationDropdown/NotificationDropdown';
import { useNotifications } from '../../../src/notifications/hooks/useNotifications';
import { useMenuContext } from '../../../src/layout/contexts/MenuContext';
import { useEconomyStore } from '../../../src/shared/stores/economyStore';
import { XpHeaderCluster } from '../../../src/economy/components/XpHeaderCluster';
import { ARENA_PATH } from '../../../src/notifications/constants/notifications.constants';
import { EXPAND_CHALLENGE_PARAM } from '../../../src/challenges/constants/challenges.constants';
import type { Notification } from '../../../src/notifications/types/notifications.types';

/** Notification types that deep-link into the Arena (mirrors the app's PageHeader). */
const CHALLENGE_NOTIFICATION_TYPES = new Set([
  'CHALLENGE_INVITE',
  'CHALLENGE_ACCEPTED',
  'CHALLENGE_DECLINED',
  'CHALLENGE_CANCELED',
  'CHALLENGE_STARTED',
  'CHALLENGE_REMIND',
  'CHALLENGE_COMPLETED',
]);

interface WarmupHeaderProps {
  showBack: boolean;
  onBack: () => void;
  /** Show the leaderboard trophy (hub only — hidden while a game is open). */
  showLeaderboard: boolean;
  onOpenLeaderboard: () => void;
}

export function WarmupHeader({ showBack, onBack, showLeaderboard, onOpenLeaderboard }: WarmupHeaderProps) {
  const insets = useSafeAreaInsets();
  const router = useRouter();
  const coins = useEconomyStore((s) => s.coins);
  const coinsLoaded = useEconomyStore((s) => s.coinsLoaded);
  const { handleMenuPress } = useMenuContext();
  const { notifications, unreadCount, markRead, markAllRead } = useNotifications();
  const [showNotifications, setShowNotifications] = useState(false);

  const handleBellPress = useCallback(() => setShowNotifications((prev) => !prev), []);
  const handleCloseNotifications = useCallback(() => setShowNotifications(false), []);
  // Tapping the avatar opens the self-profile (specs/profile-page); switching moved to the menu.
  const handleAvatarPress = useCallback(() => router.push('/app/profile'), [router]);

  const handleNotificationPress = useCallback(
    (notification: Notification) => {
      if (CHALLENGE_NOTIFICATION_TYPES.has(notification.type)) {
        const href = notification.linkId
          ? `${ARENA_PATH}?${EXPAND_CHALLENGE_PARAM}=${encodeURIComponent(notification.linkId)}`
          : ARENA_PATH;
        router.push(href as never);
      }
      handleCloseNotifications();
    },
    [router, handleCloseNotifications],
  );

  return (
    <>
      <View style={styles.container}>
        <View style={styles.left}>
          {showBack ? (
            <Pressable onPress={onBack} style={({ pressed }) => [styles.backButton, pressed && styles.pressed]}>
              <ChevronLeft size={20} color={WARMUP_COLORS.text} />
            </Pressable>
          ) : null}
          <XpHeaderCluster onAvatarPress={handleAvatarPress} testID="warmup-header-avatar" />
          <CreateCoinBalanceChip coins={coins} loaded={coinsLoaded} />
        </View>

        <View style={styles.right}>
          {showLeaderboard ? <GameLeaderboardTrophy onPress={onOpenLeaderboard} /> : null}
          <NotificationBell unreadCount={unreadCount} onPress={handleBellPress} />
          <Pressable
            onPress={handleMenuPress}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            style={({ pressed }) => [styles.iconButton, pressed && styles.pressed]}
            accessibilityRole="button"
            accessibilityLabel="Open menu"
          >
            <Menu size={24} color={WARMUP_COLORS.text} />
          </Pressable>
        </View>
      </View>

      {showNotifications ? (
        <NotificationDropdown
          notifications={notifications}
          unreadCount={unreadCount}
          onClose={handleCloseNotifications}
          onMarkRead={markRead}
          onMarkAllRead={markAllRead}
          onNotificationPress={handleNotificationPress}
          topOffset={insets.top + 64}
        />
      ) : null}
    </>
  );
}
